import type { BranchRepresentationType } from '@readme/api-contracts/schemas/branch';
import type React from 'react';

import { isInArray } from '@readme/backend/iso';
import { useCallback, useEffect, useRef } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import useAmplitude, { AMPLITUDE_EVENT } from '@core/hooks/useAmplitude';
import useBuildRoute from '@core/hooks/useBuildRoute';
import useDashLinkUrl from '@core/hooks/useDashLinkUrl';
import useSectionContentStatus from '@core/hooks/useSectionContentStatus';
import { useProjectStore, useSuperHubStore, superHubStore } from '@core/store';
import { ORDER_YAML_REGEX } from '@core/store/SuperHub/Branches/utils';
import { isGuidesPage, isReferencePage } from '@core/store/SuperHub/Document/util';
import { isBranch } from '@core/store/SuperHub/util';
import { getProjectFirstPagePath } from '@routes/SuperHub/RedirectToProjectFirstPage';
import { SuperHubHashRoutePaths, type SuperHubHashRouteParams } from '@routes/SuperHub/types';

import { useSuperHubContext } from '../components/Context';
import { useBlockingModalContext } from '../Versions/Modals/Blocking';

type SuperHubHashPath = SuperHubHashRouteParams['action'] | `${SuperHubHashRouteParams['action']}/${string}`;
const routeSectionToContentStatusKey: Partial<
  Record<string, 'changelog' | 'custom_pages' | 'guides' | 'recipes' | 'reference'>
> = {
  changelog: 'changelog',
  docs: 'guides',
  page: 'custom_pages',
  recipes: 'recipes',
  reference: 'reference',
};

function isKeyboardEventFromEditableField(e: KeyboardEvent) {
  const { target } = e;
  if (!target || !(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target.closest('[contenteditable="true"]')) return true;
  const { tagName } = target;
  return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
}

/** Keys handled by `handleNavHotkeys` when Ctrl is held. */
const CTRL_NAV_HOTKEY_KEYS = new Set([',', '.', '/', '1', '2', '3', 'i', 'm', 't']);

/**
 * Determines if the provided slug is valid by checking to make sure it has a
 * value and that it isn't a `_order.yaml` file that should never be used.
 */
const isValidSlug = (slug: string) => {
  return !!slug && !ORDER_YAML_REGEX.test(slug);
};

const matchHashSubpath = (hash: string) => {
  const match = matchPath<SuperHubHashRouteParams>(hash?.substring(1), Object.values(SuperHubHashRoutePaths));
  return {
    action: match?.params.action,
    section: match?.params.section,
  };
};

export default function useNavigationHandlers() {
  const { hash, pathname, search } = useLocation();
  const { track } = useAmplitude();
  const buildRoute = useBuildRoute();

  const [
    currentBranch,
    slug,
    doc,
    isViewing,
    isEditing,
    isReviewing,
    routeSection,
    findSidebarFirstPage,
    isSuperHubAdmin,
    diffPage,
    isEnterpriseChild,
    toggleJumpBox,
    toggleAdminUIHidden,
    toggleAIPanel,
    isAdminUIHidden,
    isViewingBranch,
  ] = useSuperHubStore(s => [
    s.currentBranch,
    s.slug || '',
    s.document.data,
    !s.routeAction,
    s.isEditing,
    s.routeAction === 'review',
    s.routeSection,
    s.sidebar.findSidebarFirstPage,
    s.user.role === 'admin',
    s.branches.getDiffPage(),
    s.enterprise?.isChild,
    s.toggleJumpBox,
    s.toggleAdminUIHidden,
    s.toggleAIPanel,
    s.isAdminUIHidden,
    isBranch(s.currentBranch),
  ]);

  const [onboardingCompleted, isTrialExpired] = useProjectStore(s => [
    s.data.onboarding_completed,
    s.plan.isTrialExpired,
  ]);
  const { browserRouterHistory } = useSuperHubContext();
  const { setBlockingModalValue } = useBlockingModalContext();
  const dashLinkUrl = useDashLinkUrl();
  const { revalidate: revalidateSectionStatus } = useSectionContentStatus();

  const isViewableSection = ['changelog', 'docs', 'page', 'reference', 'recipes', 'discuss'].includes(
    routeSection ?? '',
  );
  const isEditableSection = ['changelog', 'docs', 'page', 'reference', 'recipes'].includes(routeSection ?? '');
  const isReviewableSection = ['docs', 'page', 'reference', 'recipes'].includes(routeSection ?? '');
  const isMyDevelopers = matchHashSubpath(hash).action === 'my-developers';
  const pendingPreviewRedirectRef = useRef(false);
  const getRedirectSlug = useCallback(
    (slugToValidate: string) => {
      const isGuideReferencePage = isGuidesPage(doc) || isReferencePage(doc);
      const isGuideReferenceLink = isGuideReferencePage && doc.type === 'link';
      const isGuideReferenceSectionWithoutSlug = isInArray(routeSection, ['docs', 'reference']) && !slugToValidate;

      // if we are navigating to the guides or reference section and do not have
      // a slug, find the first slug in the section's sidebar
      if (isGuideReferenceLink || isGuideReferenceSectionWithoutSlug) {
        return findSidebarFirstPage()?.slug || '';
      }

      // if we are coming from the review tab for a page that was deleted, we
      // should not attempt to redirect to that slug since it no longer exists
      if (isReviewing && diffPage?.merge?.diff.status === 'deleted') {
        return '';
      }

      return isValidSlug(slugToValidate) ? slugToValidate : '';
    },
    [diffPage?.merge?.diff.status, doc, findSidebarFirstPage, isReviewing, routeSection],
  );

  const goToEdit = useCallback(
    (event?: React.MouseEvent) => {
      event?.preventDefault();

      // If we are already on the edit tab, don't do anything
      if (isEditing) {
        return;
      }

      track(AMPLITUDE_EVENT.PROJECT_EDIT_STARTED);

      // If the user is navigated to an editable page, we take them to that page in the editor.
      let entrySection = routeSection;
      if (!isEditableSection) {
        if (routeSection === 'api_definitions') {
          // if we are coming from the api_definitions section on the review tab,
          // the next closest section available on the edit tab is reference
          entrySection = 'reference';
        } else {
          // if the section is some other non-editable value, default to docs
          entrySection = 'docs';
        }
      }
      const redirectSlug = isEditableSection ? getRedirectSlug(slug) : '';

      browserRouterHistory.push({
        pathname: buildRoute({
          pathnameSegments: ['update', entrySection, redirectSlug],
        }),
        hash: '#',
      });
    },
    [browserRouterHistory, buildRoute, getRedirectSlug, isEditableSection, isEditing, routeSection, slug, track],
  );

  const goToPreview = useCallback(
    async (event?: React.MouseEvent) => {
      event?.preventDefault();

      // Revalidate section content status before switching to view mode so
      // we have fresh data after any creates/deletes in edit mode.
      const freshStatus = await revalidateSectionStatus();

      // When switching to "View" mode from another route where the section
      // is viewable, redirect to the corresponding page.
      if (isViewableSection || routeSection === 'api_definitions') {
        // if we are coming from the api_definitions section on the review tab,
        // the next closest section available on the view tab is reference
        const redirectSection = routeSection === 'api_definitions' ? 'reference' : routeSection;

        // If the section is empty, redirect to the project's first page.
        const contentKey = routeSectionToContentStatusKey[redirectSection ?? ''];
        const isSectionEmpty = freshStatus && contentKey && freshStatus[contentKey] === false;
        if (isSectionEmpty) {
          browserRouterHistory.push({
            pathname: getProjectFirstPagePath(freshStatus),
            hash: '#',
          });
          return;
        }

        const redirectSlug = isViewableSection ? getRedirectSlug(slug) : '';
        const redirectPath = buildRoute({
          pathnameSegments: [redirectSection, redirectSlug],
        });

        browserRouterHistory.push({
          pathname: redirectPath,
          hash: '#',
        });
      } else {
        // When switching to "View" mode from a review route where the section
        // is not viewable (e.g. api_definitions, custom_components), redirect
        // them to the project's first page.
        browserRouterHistory.push({
          pathname: getProjectFirstPagePath(freshStatus),
          hash: '#',
        });
      }
    },
    [browserRouterHistory, buildRoute, getRedirectSlug, isViewableSection, revalidateSectionStatus, routeSection, slug],
  );

  const togglePreviewMode = useCallback(async () => {
    const navigateForPreviewToggle = (targetPath: string) => {
      // If we're already on the destination path, pushing the same route may not
      // trigger a render. Toggle immediately instead of waiting on pending state.
      if (targetPath === pathname) {
        pendingPreviewRedirectRef.current = false;
        toggleAdminUIHidden();
        return;
      }

      pendingPreviewRedirectRef.current = true;
      browserRouterHistory.push({
        pathname: targetPath,
        hash: '#',
      });
    };

    if (isAdminUIHidden) {
      const routeToRestore = superHubStore.getState().previousPreviewEntry;
      superHubStore.getState().setPreviousPreviewEntry(null);
      if (routeToRestore) {
        browserRouterHistory.push(routeToRestore);
      }
      toggleAdminUIHidden();
      pendingPreviewRedirectRef.current = false;
      return;
    }

    // In view mode, ensure we do not hide admin UI on an empty section that
    // would be inaccessible to non-admin viewers.
    if (isViewing) {
      const freshStatus = await revalidateSectionStatus();
      const contentKey = routeSectionToContentStatusKey[routeSection ?? ''];
      const isSectionEmpty = !!(freshStatus && contentKey && freshStatus[contentKey] === false);
      if (isSectionEmpty) {
        navigateForPreviewToggle(getProjectFirstPagePath(freshStatus));
        return;
      }
    }

    // If we're editing or reviewing, go to a view route first so hidden admin
    // UI still has page content behind it.
    if (isEditing || isReviewing) {
      superHubStore.getState().setPreviousPreviewEntry({ hash, pathname, search });
      goToPreview();
      return;
    }

    toggleAdminUIHidden();
  }, [
    goToPreview,
    isAdminUIHidden,
    isEditing,
    isReviewing,
    toggleAdminUIHidden,
    routeSection,
    browserRouterHistory,
    hash,
    search,
    isViewing,
    pathname,
    revalidateSectionStatus,
  ]);

  // Enter hidden preview mode after completing a redirect from view mode.
  useEffect(() => {
    if (pendingPreviewRedirectRef.current && isViewing && !isAdminUIHidden) {
      pendingPreviewRedirectRef.current = false;
      toggleAdminUIHidden();
    }
  }, [isAdminUIHidden, isViewing, pathname, toggleAdminUIHidden, slug]);

  // Enter hidden preview mode after switching from edit/review to view mode.
  useEffect(() => {
    const previousPreviewEntry = superHubStore.getState().previousPreviewEntry;
    if (previousPreviewEntry && isViewing && !isAdminUIHidden) {
      toggleAdminUIHidden();
    }
  }, [isAdminUIHidden, isViewing, pathname, toggleAdminUIHidden, slug]);

  useEffect(() => {
    if (!isAdminUIHidden || !isViewing || !routeSection) {
      return;
    }

    let isCancelled = false;
    // In hidden preview mode, keep the current route viewer-safe by
    // redirecting away from sections with no viewable content.
    const guardHiddenPreviewSection = async () => {
      const contentKey = routeSectionToContentStatusKey[routeSection];
      const freshStatus = contentKey ? await revalidateSectionStatus() : null;
      const isSectionEmpty = !!(freshStatus && contentKey && freshStatus[contentKey] === false);

      if (isCancelled || !isSectionEmpty) {
        return;
      }

      const safeFallbackPath = getProjectFirstPagePath(freshStatus);
      if (pathname === safeFallbackPath) {
        return;
      }

      browserRouterHistory.push({
        pathname: safeFallbackPath,
        hash: '#',
      });
    };

    guardHiddenPreviewSection().catch(() => {});

    return () => {
      isCancelled = true;
    };
  }, [browserRouterHistory, isAdminUIHidden, isViewing, pathname, revalidateSectionStatus, routeSection]);

  const goToReview = useCallback(
    ({
      branch,
    }: {
      /** Branch name to review. Defaults to current branch if not provided. */
      branch?: BranchRepresentationType['name'];
    } = {}) => {
      /**
       * Indicates which branch to review: either the provided branch or the
       * current branch if none is provided.
       */
      const branchToReview = branch || currentBranch.name;

      if (branchToReview === currentBranch.name) {
        // If we are already on the review tab, don't do anything
        if (isReviewing) return;

        let redirectPath = '/review';
        if (isReviewableSection) {
          // When switching to "Review" mode from another route where the current
          // section is reviewable, attempt to redirect to the current slug
          // on the review tab. It's possible that this slug won't be present on
          // the review tab if it does not contain any diffs, but that route will
          // display an empty state in that case.
          const redirectSlug = getRedirectSlug(slug);
          redirectPath = buildRoute({
            pathnameSegments: ['review', routeSection, redirectSlug],
          });
        }

        // Branch to review is the current branch, so we can simply re-route.
        browserRouterHistory.push({
          pathname: redirectPath,
          hash: '#',
        });
      } else {
        // Branch to review is not the current branch, so we need to redirect.
        const redirectPath = buildRoute({
          includeBaseUrl: true,
          pathnameSegments: [`v${encodeURIComponent(branchToReview)}`, 'review'],
        });
        setBlockingModalValue('Redirecting…');
        window.location.assign(redirectPath);
      }

      // Finally, track the event in either scenario.
      track(AMPLITUDE_EVENT.PROJECT_REVIEW_STARTED);
    },
    [
      browserRouterHistory,
      buildRoute,
      currentBranch.name,
      getRedirectSlug,
      isReviewableSection,
      isReviewing,
      routeSection,
      setBlockingModalValue,
      slug,
      track,
    ],
  );

  const goToMyDevelopers = useCallback(() => {
    // When my-developers control is moved to the top-right, it acts like a
    // toggle instead of a "mode" and should toggle on/off when clicked on.
    if (isMyDevelopers) {
      window.location.hash = '/';
      return;
    }

    const isSetupComplete = onboardingCompleted.logs;

    /**
     * Redirect to setup if My Developers onboarding is not complete. Only
     * admins can complete My Developers setup, so anyone with lower edit
     * permissions (editors) should go to the normal, empty view.
     */
    window.location.hash = !isSetupComplete && isSuperHubAdmin ? '/my-developers/setup' : '/my-developers';
  }, [isMyDevelopers, isSuperHubAdmin, onboardingCompleted.logs]);

  const goToAdmin = useCallback(
    (hashPath: SuperHubHashPath = 'settings') => {
      const match = matchHashSubpath(hash);

      const isActive =
        Object.values(match).join('/').startsWith(hashPath) ||
        (hashPath === 'settings' &&
          ['settings', 'appearance', 'branches', 'docs-audit', 'my-developers'].includes(match.action || ''));

      /* When a trial expires, all settings pages aside from Manage Plans are disabled  */
      const hashToNavigateTo = isTrialExpired && hashPath === 'settings' ? '#/settings/manage-plans' : `#/${hashPath}`;
      browserRouterHistory.push({
        hash: isActive ? '#/' : hashToNavigateTo,
      });
    },
    [hash, isTrialExpired, browserRouterHistory],
  );

  const toggleSidebarRailCollapsed = useCallback(() => {
    superHubStore.getState().toggleSettingsSidebarRailCollapsed();
  }, []);

  const handleNavHotkeys = useCallback(
    (e: KeyboardEvent) => {
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const isThemeEditorHotkey = e.code === 'KeyA' && e.altKey && e.shiftKey;

      if (e.key === 'Escape' && isAdminUIHidden && !isKeyboardEventFromEditableField(e)) {
        e.preventDefault();
        togglePreviewMode();
        return;
      }

      if (!isThemeEditorHotkey) {
        if (!e.ctrlKey) return;
        if (!CTRL_NAV_HOTKEY_KEYS.has(key)) return;
      }

      if (key === '3' && !isViewingBranch) return;

      if (key === '/' && isKeyboardEventFromEditableField(e)) return;

      e.preventDefault();

      if (key === '/') {
        toggleSidebarRailCollapsed();
      } else if (key === ',') {
        goToAdmin('settings/project-settings');
      } else if (key === '.') {
        togglePreviewMode();
      } else if (isThemeEditorHotkey) {
        goToAdmin('appearance/theme-editor');
      } else if (key === 't') {
        goToAdmin('settings/tools');
      } else if (key === 'i') {
        toggleAIPanel();
      } else if (key === 'm') {
        goToMyDevelopers();
      } else if (key === '1') {
        goToPreview();
      } else if (key === '2') {
        goToEdit();
      } else if (key === '3') {
        goToReview();
      }

      toggleJumpBox(false);
    },
    [
      goToAdmin,
      goToEdit,
      goToMyDevelopers,
      goToPreview,
      goToReview,
      isAdminUIHidden,
      toggleAIPanel,
      toggleJumpBox,
      togglePreviewMode,
      toggleSidebarRailCollapsed,
      isViewingBranch,
    ],
  );

  const isOnLinterRoute = useCallback(() => {
    const match = matchHashSubpath(hash);
    return match.action === 'settings' && match.section === 'linter';
  }, [hash]);

  const goToLintRules = useCallback(() => {
    if (isEnterpriseChild) {
      const url = dashLinkUrl('linter-rules', { toEnterpriseGroup: true });
      if (url) {
        window.open(url, '_blank');
      }
      return;
    }

    // Avoid pushing a redundant history entry if we're already on the linter
    // page. Mirrors the active-check in `goToAdmin`.
    if (isOnLinterRoute()) return;

    browserRouterHistory.push({
      hash: '#/settings/linter',
    });
  }, [browserRouterHistory, dashLinkUrl, isEnterpriseChild, isOnLinterRoute]);

  const goToLintRulesOrClose = useCallback(() => {
    if (isOnLinterRoute()) {
      browserRouterHistory.push({
        hash: '#/',
      });
      return;
    }

    goToLintRules();
  }, [browserRouterHistory, goToLintRules, isOnLinterRoute]);

  const goToReviewRequirements = useCallback(() => {
    const url = dashLinkUrl('projects', { toEnterpriseGroup: true });
    if (url) window.open(url, '_blank');
  }, [dashLinkUrl]);

  return {
    goToAdmin,
    goToEdit,
    goToLintRules,
    goToLintRulesOrClose,
    goToReviewRequirements,
    goToPreview,
    goToReview,
    goToMyDevelopers,
    togglePreviewMode,
    handleNavHotkeys,
  };
}
